import React, { useCallback, useEffect, useState } from 'react';
import { ErrorInfo, isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import {
  AssignmentLimitUiModel,
  AssignmentRuleUiModel
} from '../../../shared/ui-models/team.ui-model';
import {
  getAssignmentLimits,
  getAssignmentRules,
  upsertAssignmentLimit
} from '../../../shared/services/data/team.data';
import { Loader } from '../../../shared/components/core-ui/loader/loader.component';
import { birdToast } from '../../../shared/components/core-ui/toast';
import { TEAM_ROLES } from '../team-structure/components/member-form-modal.component';

export const AssignmentSettingsContainer: React.FC = () => {
  const [limits, setLimits] = useState<AssignmentLimitUiModel[]>([]);
  const [rules, setRules] = useState<AssignmentRuleUiModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const load = useCallback(() => {
    setLoading(true);
    return Promise.all([getAssignmentLimits(), getAssignmentRules()]).then(
      ([limitsResult, rulesResult]) => {
        if (!isErrorInfo(limitsResult)) setLimits(limitsResult);
        if (!isErrorInfo(rulesResult)) setRules(rulesResult);
        setLoading(false);
      }
    );
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const limitFor = (role: string): AssignmentLimitUiModel | undefined =>
    limits.find((limit) => limit.role === role);

  const handleSaveLimit = (role: string) => {
    const raw = (drafts[role] ?? '').trim();
    // Empty means unlimited, which the service stores as null. Zero is a real
    // and different setting: it takes the role out of rotation entirely.
    const value = raw.length === 0 ? null : Number(raw);
    if (value !== null && (!Number.isInteger(value) || value < 0)) {
      birdToast.error('A cap must be a whole number, or blank for unlimited');
      return;
    }

    const promise = upsertAssignmentLimit(role, value).then((response: boolean | ErrorInfo) => {
      if (isErrorInfo(response)) throw response;
      return load();
    });

    birdToast.promise(promise, {
      loadingMessage: 'Saving cap...',
      successMessage: 'Cap saved',
      errorPermissionMessage: 'You do not have permission to change assignment caps',
      errorFailedMessage: 'Failed to save cap'
    });
  };

  return (
    <div className="flex h-full flex-col items-stretch md:min-h-[calc(100vh-45px)] md:bg-[#f5f7fc] md:px-[2rem] md:py-[3rem]">
      <h1 className="mb-4 text-xl font-semibold">Assignment</h1>

      {loading ? (
        <Loader />
      ) : (
        <>
          <div className="mb-6 rounded bg-white p-4 shadow-sm">
            <h2 className="text-sm font-medium">Active-lead cap per role</h2>
            <p className="mb-3 text-xs text-gray-500">
              How many open leads one person of this role may hold. When everyone in a matched
              rule&apos;s pool is at their cap the lead is left <strong>unassigned</strong> rather
              than forced onto someone — it stays visible in the queue to be picked up. Blank means
              unlimited; 0 takes the role out of rotation.
            </p>

            {TEAM_ROLES.map((role) => {
              const limit = limitFor(role.value);
              const draft =
                drafts[role.value] ??
                (limit && limit.maxActiveLeads !== null ? String(limit.maxActiveLeads) : '');
              return (
                <div key={role.value} className="flex items-center gap-3 border-b border-gray-100 py-2">
                  <span className="w-32 text-sm">{role.label}</span>
                  <input
                    className="w-24 rounded border border-gray-300 px-2 py-1"
                    value={draft}
                    placeholder="unlimited"
                    onChange={(event) =>
                      setDrafts({ ...drafts, [role.value]: event.target.value })
                    }
                  />
                  <span className="text-xs text-gray-400">
                    {!limit || limit.isUnlimited ? 'currently unlimited' : `currently ${limit.maxActiveLeads}`}
                  </span>
                  <button
                    type="button"
                    className="ml-auto text-sm text-blue-600"
                    onClick={() => handleSaveLimit(role.value)}
                  >
                    Save
                  </button>
                </div>
              );
            })}
          </div>

          <div className="rounded bg-white p-4 shadow-sm">
            <h2 className="text-sm font-medium">Routing rules</h2>
            <p className="mb-3 text-xs text-gray-500">
              The most specific matching rule wins — rules are tried by number of criteria,
              descending, so a catch-all is only reached when nothing narrower fits. A matched rule
              is the only one used: assignment does <strong>not</strong> fall through to a broader
              rule when its pool is full.
            </p>

            {rules.length === 0 ? (
              <p className="py-6 text-center text-gray-500">
                No rules yet. Without one, leads arrive unassigned.
              </p>
            ) : (
              rules.map((rule) => (
                <div key={rule.id} className="flex flex-wrap items-center gap-3 border-b border-gray-100 py-3">
                  <span className="font-medium">{rule.name}</span>
                  {rule.isCatchAll ? (
                    <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-800">
                      catch-all
                    </span>
                  ) : (
                    Object.keys(rule.criteria).map((key) => (
                      <span key={key} className="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700">
                        {key}: {String(rule.criteria[key])}
                      </span>
                    ))
                  )}
                  {rule.skipCap ? (
                    <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs text-red-700">
                      ignores caps
                    </span>
                  ) : null}
                  {!rule.isActive ? (
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
                      inactive
                    </span>
                  ) : null}
                  <span className="ml-auto text-xs text-gray-500">
                    {rule.assigneeNames.length
                      ? rule.assigneeNames.join(', ')
                      : 'no assignees — this rule can never assign'}
                  </span>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};
